import { useEffect, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { get, post } from './lib/api';
import { Button, Card, ErrorNote, Field, Input, Loading } from './ui';
import Accounts from './pages/Accounts';
import Account from './pages/Account';
import Audit from './pages/Audit';
import Settings from './pages/Settings';
import { ErrorBoundary } from './components/ErrorBoundary';

/**
 * One password, one door.
 *
 * This is an agency-internal tool that holds other people's advertising data.
 * It does not need a user table; it needs to not be open to the internet.
 */

export function App() {
  const me = useQuery({ queryKey: ['me'], queryFn: () => get('/v1/me'), retry: false, staleTime: 60_000 });

  if (me.isLoading) return <div className="p-10"><Loading label="Opening" /></div>;
  if (me.error) return <Login onIn={() => me.refetch()} />;

  return (
    <div className="min-h-full">
      <header className="border-b border-bd bg-bg2">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-6 py-3">
          <Link to="/" className="flex items-center gap-2">
            <span className="flex size-7 items-center justify-center rounded-lg bg-ink text-xs font-bold text-on-ink">A</span>
            <span className="text-sm font-bold">Ads Audit</span>
          </Link>
          <Link to="/settings" className="ml-auto text-xs text-t3 hover:text-t1">Settings</Link>
          <button
            onClick={async () => { await post('/v1/auth/logout'); location.reload(); }}
            className="text-xs text-t3 hover:text-t1"
          >
            Sign out
          </button>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-6 py-8">
        {/* Inside the shell, so a broken page keeps the header and a way out
            of it. A crash used to take the entire interface with it. */}
        <Boundary>
          <Routes>
            <Route index element={<Accounts />} />
            <Route path="accounts/:id" element={<Account />} />
            <Route path="audits/:id" element={<Audit />} />
            <Route path="settings" element={<Settings />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Boundary>
      </main>
    </div>
  );
}

/** Keyed on the path, so a failure on one screen does not stick to the next. */
function Boundary({ children }: { children: React.ReactNode }) {
  const loc = useLocation();
  return <ErrorBoundary key={loc.pathname} where={loc.pathname}>{children}</ErrorBoundary>;
}

function Login({ onIn }: { onIn: () => void }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  useEffect(() => { document.title = 'Ads Audit'; }, []);

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <Card className="w-full max-w-sm">
        <div className="mb-5">
          <span className="flex size-9 items-center justify-center rounded-lg bg-ink text-sm font-bold text-on-ink">A</span>
          <h1 className="mt-3 text-lg font-bold">Ads Audit</h1>
          <p className="text-sm text-t2">Find the holes in a Google Ads account before the meeting.</p>
        </div>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setError(null);
            try {
              await post('/v1/auth/login', { password });
              onIn();
              nav('/');
            } catch (err) {
              setError(err);
            } finally {
              setBusy(false);
            }
          }}
        >
          <Field label="Password">
            <Input type="password" autoFocus autoComplete="current-password" value={password}
              onChange={(e) => setPassword(e.target.value)} />
          </Field>
          <ErrorNote error={error} />
          <Button type="submit" variant="primary" loading={busy} disabled={!password} className="w-full">
            Open
          </Button>
        </form>
        {/* Plain <a>, not <Link>: these are static pages nginx serves outside
            the app, and a router Link would try to render them as a route. */}
        <div className="mt-5 flex gap-4 border-t border-bd pt-4 text-xs text-t3">
          <a href="/privacy" className="hover:text-t1">Privacy</a>
          <a href="/terms" className="hover:text-t1">Terms</a>
        </div>
      </Card>
    </div>
  );
}
